import type {
  AmbassadorApplicationInput,
  AmbassadorApplicationStatus,
  AmbassadorBookingRow,
  AmbassadorLinkRow,
  AmbassadorLinkStats,
  AmbassadorMe,
  AmbassadorProfilePatch,
  AmbassadorPublicProfile,
  AmbassadorSummary,
} from "@majourneys/shared";
import { apiBase, apiHeaders } from "../../../lib/api-base";

/**
 * A failed ambassador call. `code` is the API's `error.message`
 * (`handle_taken`, `invalid_category`, `ambassador_not_active`, ...), which
 * is what the pages switch on; never shown to a guest as is.
 */
export class AmbassadorApiError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(code);
    this.name = "AmbassadorApiError";
  }
}

export function ambassadorErrorCode(err: unknown): string | null {
  return err instanceof AmbassadorApiError ? err.code : null;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, {
    ...init,
    credentials: "include",
    headers: apiHeaders(init.body ? { "Content-Type": "application/json" } : {}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { message?: string; code?: string };
      message?: string;
    };
    throw new AmbassadorApiError(res.status, body?.error?.message ?? body?.message ?? String(res.status));
  }
  return (await res.json()) as T;
}

const q = (lang: string) => `lang=${encodeURIComponent(lang)}`;

export type PickableCategory = { id: string; slug: string; name: string };

export const ambassadorsApi = {
  profile: (handle: string, lang: string) =>
    call<AmbassadorPublicProfile>(`/ambassadors/profile/${encodeURIComponent(handle)}?${q(lang)}`),
  categories: (lang: string) => call<PickableCategory[]>(`/ambassadors/categories?${q(lang)}`),
  me: (lang: string) => call<AmbassadorMe>(`/ambassadors/me?${q(lang)}`),
  apply: (input: AmbassadorApplicationInput) =>
    call<{ applicationId: string; status: AmbassadorApplicationStatus }>(`/ambassadors/apply`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  patchMe: (patch: AmbassadorProfilePatch) =>
    call<{ ok: true }>(`/ambassadors/me`, { method: "PATCH", body: JSON.stringify(patch) }),
  summary: () => call<AmbassadorSummary>(`/ambassadors/me/summary`),
  bookings: (lang: string) => call<AmbassadorBookingRow[]>(`/ambassadors/me/bookings?${q(lang)}`),
  links: (search: string, lang: string) =>
    call<AmbassadorLinkRow[]>(`/ambassadors/me/links?q=${encodeURIComponent(search)}&${q(lang)}`),
  linkStats: (lang: string) => call<AmbassadorLinkStats>(`/ambassadors/me/links/stats?${q(lang)}`),
  pins: (activityIds: string[]) =>
    call<{ ok: true }>(`/ambassadors/me/favourites/pins`, { method: "PUT", body: JSON.stringify({ activityIds }) }),
  favourite: (activityId: string, on: boolean) =>
    call<{ ok: true }>(`/ambassadors/me/favourites/${encodeURIComponent(activityId)}`, {
      method: on ? "PUT" : "DELETE",
    }),
};
